import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.css'],
    standalone: false
})
export class LoginComponent implements OnInit {
  loginForm!: FormGroup;
  signupForm!: FormGroup;
  loading = false;
  sendingOtp = false;
  submittingSignup = false;
  submitted = false;
  signupSubmitted = false;
  otpSent = false;
  errorMessage = '';
  signupErrorMessage = '';
  signupSuccessMessage = '';
  isSignupMode = false;

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Redirect to home if already logged in
    if (this.authService.isAuthenticated()) {
      this.router.navigate(['/']);
    }

    this.loginForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      otp: ['', [Validators.required, Validators.minLength(1)]]
    });

    this.signupForm = this.formBuilder.group({
      firstName: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(50)]],
      lastName: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(50)]],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.pattern(/^[\d\-\+\(\)\s]+$/)]]
    });
  }

  get f() {
    return this.loginForm.controls;
  }

  get sf() {
    return this.signupForm.controls;
  }

  get isEmailValid(): boolean {
    return this.f['email'].valid && this.f['email'].value.length > 0;
  }

  sendOtp(): void {
    this.errorMessage = '';
    
    if (!this.isEmailValid) {
      return;
    }

    this.sendingOtp = true;

    this.authService.sendOtp({ email: this.f['email'].value }).subscribe({
      next: () => {
        this.otpSent = true;
        this.sendingOtp = false;
      },
      error: (error) => {
        console.error('Send OTP error', error);
        if (error.status === 429) {
          const remainingMinutes = error.error?.remainingMinutes || 'a few';
          this.errorMessage = `Account is temporarily locked. Try again in ${remainingMinutes} minutes.`;
        } else if (error.status === 500) {
          this.errorMessage = 'An error occurred. Please try again later or contact support.';
        } else {
          this.errorMessage = error.error?.message || 'Failed to send OTP. Please try again.';
        }
        this.sendingOtp = false;
      }
    });
  }

  onSubmit(): void {
    this.submitted = true;
    this.errorMessage = '';

    // Stop if form is invalid
    if (this.loginForm.invalid) {
      return;
    }

    this.loading = true;

    this.authService.login({
      email: this.f['email'].value,
      otp: this.f['otp'].value
    }).subscribe({
      next: (response) => {
        console.log('Login successful', response);
        this.router.navigate(['/']);
      },
      error: (error) => {
        console.error('Login error', error);
        if (error.status === 401) {
          this.errorMessage = error.error?.message || 'Invalid OTP. Please try again.';
        } else if (error.status === 429) {
          const lockoutMinutes = error.error?.lockoutMinutes || 'a few';
          this.errorMessage = `Account is temporarily locked. Try again after ${lockoutMinutes} minutes.`;
        } else {
          this.errorMessage = error.error?.message || 'Login failed. Please try again.';
        }
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  resetOtp(): void {
    this.otpSent = false;
    this.submitted = false;
    this.errorMessage = '';
    this.f['otp'].reset();
  }

  toggleSignupMode(): void {
    this.isSignupMode = !this.isSignupMode;
    this.errorMessage = '';
    this.signupErrorMessage = '';
    this.signupSuccessMessage = '';
    this.submitted = false;
    this.signupSubmitted = false;
  }

  onSignupSubmit(): void {
    this.signupSubmitted = true;
    this.signupErrorMessage = '';
    this.signupSuccessMessage = '';

    if (this.signupForm.invalid) {
      return;
    }

    this.submittingSignup = true;

    const signupRequest = {
      firstName: this.sf['firstName'].value.trim(),
      lastName: this.sf['lastName'].value.trim(),
      email: this.sf['email'].value.trim(),
      phone: this.sf['phone'].value?.trim() || undefined
    };

    this.authService.signup(signupRequest).subscribe({
      next: () => {
        this.signupSuccessMessage = 'Account created successfully! A one-time password has been sent to your email.';
        this.submittingSignup = false;
        // Switch back to login mode after a short delay
        setTimeout(() => {
          this.isSignupMode = false;
          this.signupForm.reset();
          this.signupSubmitted = false;
          // Pre-fill the email in login form and skip to OTP entry
          this.f['email'].setValue(signupRequest.email);
          this.otpSent = true;
        }, 3000);
      },
      error: (error) => {
        console.error('Signup error', error);
        if (error.status === 409) {
          this.signupErrorMessage = error.error?.message || 'A user with this email already exists. Please login instead.';
        } else if (error.status === 500) {
          this.signupErrorMessage = 'An error occurred. Please try again later or contact support.';
        } else {
          this.signupErrorMessage = error.error?.message || 'Signup failed. Please try again.';
        }
        this.submittingSignup = false;
      }
    });
  }

  onEmailEnter(event: any): void {
    if (!this.otpSent && this.isEmailValid && !this.sendingOtp) {
      event.preventDefault();
      this.sendOtp();
    }
  }
}

